import { useRef } from 'react';
import Markdown from 'react-markdown';
import styled from 'styled-components';
import { Typography } from 'components/generic/typography';
import FlexColumn from 'components/Layout/components/Flex/components/FlexColumn';
import { AIIcon } from '../shared/styles';
import type { AIMessage as AIMessageType } from '../shared/types';
import useComAIStore, { useIsComAILoading } from '../store/comAIStore';
import InSessionFeedback from './InSessionFeedback';
import MetadataRenderer from './MetadataRenderer';

type AIMessageProps = {
    id: string;
};

const StyledMetadataRenderer = styled(MetadataRenderer)``;
const StyledInSessionFeedback = styled(InSessionFeedback)``;

const StyledFlexColumn = styled(FlexColumn)`
    &, ${StyledMetadataRenderer}, ${StyledInSessionFeedback} {
        opacity: 1;
        transform: translateY(0);
        transition: opacity 500ms ease-out, transform 500ms ease-out;

        @starting-style {
            opacity: 0;
            transform: translateY(10px);
        }
    }
`;

const MarkdownBody = (props: React.ComponentProps<typeof Markdown>) => (
    <Markdown components={{ p: (pProps) => <Typography.Body {...pProps} /> }} {...props} />
);

export default function AIMessage({ id }: AIMessageProps) {
    const chunks = useComAIStore((state) => (state.chat[id] as AIMessageType)?.chunks);
    const newestAIMessageId = useComAIStore((state) => state.newestAIMessageId);
    const isLoading = useIsComAILoading();
    const partialTextRef = useRef<string>('');

    if (!chunks) {
        return null;
    }

    return (
        <StyledFlexColumn gap="8px">
            <AIIcon type="AI" size={24} fill="white" />
            <FlexColumn>
                {chunks.map((chunk, index, array) => {
                    switch (chunk.author) {
                        case 'RootResponseAgent':
                        case 'WELCOMING_AGENT': {
                            const chunkText = chunk.content?.parts?.map((part) => part.text).join('');

                            if (chunk.partial) {
                                const isLastPartial = index === array.length - 1;
                                partialTextRef.current = partialTextRef.current + chunkText;

                                if (!isLastPartial) {
                                    return null;
                                }

                                // Render the accumulated partial text
                                return <MarkdownBody key={chunk.id}>{partialTextRef.current}</MarkdownBody>;
                            }

                            partialTextRef.current = '';

                            // Render the complete chunk text
                            return <MarkdownBody key={chunk.id}>{chunkText}</MarkdownBody>;
                        }
                        case 'CHIPS_AGENT':
                        case 'CHIPS_AGENT_CAROUSEL':
                        case 'CHIPS_AGENT_PROBLEM_SOLVE':
                        case 'CONCEPTUAL_CHIP_AGENT':
                            return <StyledMetadataRenderer chunk={chunk} key={chunk.id} />;
                        default:
                            return null;
                    }
                })}
            </FlexColumn>
            {!isLoading && newestAIMessageId === id && <StyledInSessionFeedback invocationId={id} />}
        </StyledFlexColumn>
    );
}
